import { CompaniesTabView, useCompaniesFundsFilter } from '@valiant/ui-shared'
import type { CompanyData, GroupOption, CreateCompanyInput } from '@valiant/ui-shared'
import {
  useGetCompaniesQuery,
  useGetGroupsQuery,
  useCreateCompanyMutation,
  useAssignUserToCompanyMutation,
  useRemoveUserFromCompanyMutation,
  useGetCompanyDepositWalletsQuery,
  CompanyStatus,
} from '../../generated/graphql'
import { useState } from 'react'
import { toast } from 'sonner'
import { useNavigate } from 'react-router-dom'
import { useNavigation } from '../../contexts/NavigationContext'

interface CompaniesTabProps {
  groupId?: string
  userId?: string
  searchTerm?: string
  hideUnassignButton?: boolean
  hideAssignButton?: boolean
}

export function CompaniesTab({
  groupId,
  userId,
  searchTerm,
  hideUnassignButton = false,
  hideAssignButton = false,
}: CompaniesTabProps = {}) {
  const navigate = useNavigate()
  const { setSelectedGroupId } = useNavigation()

  const [companyId, setCompanyId] = useState<string | null>(null)
  const { fundsFilter, setFundsFilter } = useCompaniesFundsFilter()

  // GraphQL queries
  const [{ data: companiesData, fetching: companiesLoading }] = useGetCompaniesQuery({
    variables: {
      filter: {
        ...(groupId && { groupId }),
        ...(userId && { userId }),
        ...(searchTerm && { search: searchTerm }),
        ...(fundsFilter === 'withFunds' && { hasFunds: true }),
      },
    },
  })

  // Query for all companies (for Assign dialog)
  const [{ data: allCompaniesData }] = useGetCompaniesQuery({
    variables: {
      filter: {},
    },
  })

  const [{ data: groupsData }] = useGetGroupsQuery()
  const [{ data: walletsData, fetching: walletsLoading }, fetchWallets] =
    useGetCompanyDepositWalletsQuery({
      pause: !companyId,
      variables: {
        companyId: companyId || '',
      },
    })

  // Mutations
  const [, createCompany] = useCreateCompanyMutation()
  const [, assignUserToCompany] = useAssignUserToCompanyMutation()
  const [, removeUserFromCompany] = useRemoveUserFromCompanyMutation()

  // Data mapping
  const companies: CompanyData[] = (companiesData?.getCompanies || []).map((company) => ({
    id: company.id,
    company: company.company,
    group: company.group,
    groupId: company.groupId,
    imageUrl: company.imageUrl || undefined,
    tradeType: company.tradeType as 'CRYPTO' | 'CRYPTO_AND_FIAT' | 'DEACTIVATED',
    autoApprove: company.autoApprove,
    balance: company.balance,
  }))

  // Map all companies for Assign dialog
  const allCompanies = (allCompaniesData?.getCompanies || [])
    .filter((company) => !companies.some((c) => c.company === company.company))
    .map((company) => ({
      id: company.id,
      company: company.company,
      group: company.group,
      groupId: company.groupId,
    }))

  const groups: GroupOption[] = (groupsData?.getGroups || []).map((group) => ({
    groupId: group.groupId,
    name: group.name,
    status: group.status as 'ACTIVE' | 'DEACTIVATED',
  }))

  const wallets = walletsData?.getCompanyDepositWallets || []

  // Helper function to map UI tradeType to GraphQL CompanyStatus
  const mapTradeTypeToCompanyStatus = (
    tradeType: 'CRYPTO' | 'CRYPTO_AND_FIAT' | 'DEACTIVATED',
  ): CompanyStatus => {
    switch (tradeType) {
      case 'CRYPTO':
        return CompanyStatus.Crypto
      case 'CRYPTO_AND_FIAT':
        return CompanyStatus.CryptoAndFiat
      case 'DEACTIVATED':
        return CompanyStatus.Deactivated
      default:
        return CompanyStatus.Crypto
    }
  }

  // Callback implementations
  const handleCreateCompany = async (input: CreateCompanyInput) => {
    try {
      const result = await createCompany({
        input: {
          name: input.company,
          groupId: input.groupId,
          status: mapTradeTypeToCompanyStatus(input.tradeType),
        },
      })

      if (result.error) {
        toast.error('Failed to create company', {
          description: result.error.message,
        })
        return
      }

      toast.success('Company created successfully!')
    } catch (error) {
      toast.error('Failed to create company', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
      })
    }
  }

  const handleAssignUserToCompany = async (targetUserId: string, companyIds: string[]) => {
    try {
      const result = await assignUserToCompany({
        input: {
          userId: targetUserId,
          companyId: companyIds,
        },
      })
      if (result.error) {
        throw new Error(result.error.message)
      }
      const message =
        companyIds.length === 1
          ? 'User successfully assigned to company!'
          : `User successfully assigned to ${companyIds.length} companies!`
      toast.success(message)
    } catch (error) {
      toast.error('Failed to assign user to company', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
      })
    }
  }

  const handleUnassignUserFromCompany = async (targetUserId: string, companyId: string) => {
    try {
      const result = await removeUserFromCompany({
        input: {
          userId: targetUserId,
          companyId,
        },
      })
      if (result.error) {
        toast.error('Failed to unassign user from company', {
          description: result.error.message,
        })
        return
      }
      toast.success('User successfully unassigned from company!')
    } catch (error) {
      toast.error('Failed to unassign user from company', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
      })
    }
  }

  const handleViewCompanyDetails = (companyId: string) => {
    const company = companies.find((c) => c.id === companyId)
    setSelectedGroupId(company?.groupId || '')
    navigate(`/companies/${companyId}`)
  }

  const handleFetchWallets = (companyId: string) => {
    setCompanyId(companyId)
    fetchWallets({ variables: { companyId } })
  }

  const handleCopyWalletAddress = (address: string) => {
    navigator.clipboard.writeText(address)
    toast.success('Address copied to clipboard!')
  }

  return (
    <CompaniesTabView
      companies={companies}
      loading={companiesLoading}
      title={userId ? 'User Companies' : 'Companies'}
      showCreateButton={false}
      showAssignButton={!!userId}
      showWalletsButton={!userId}
      showUnassignButton={!!userId && !hideUnassignButton}
      hideAssignButton={hideAssignButton}
      groups={groups}
      allCompanies={allCompanies}
      depositWallets={wallets}
      walletsLoading={walletsLoading}
      onCreateCompany={handleCreateCompany}
      onAssignUserToCompany={
        userId ? (_userId, companyIds) => handleAssignUserToCompany(userId, companyIds) : undefined
      }
      onUnassignUserFromCompany={
        userId
          ? (_userId, targetCompanyId) => handleUnassignUserFromCompany(userId, targetCompanyId)
          : undefined
      }
      onViewCompanyDetails={handleViewCompanyDetails}
      onFetchWallets={handleFetchWallets}
      onCopyWalletAddress={handleCopyWalletAddress}
      userId={userId}
      fundsFilter={fundsFilter}
      onFundsFilterChange={setFundsFilter}
    />
  )
}
